import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ChangeEvent,
} from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Card from "@mui/material/Card";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { alpha, useTheme } from "@mui/material/styles";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";
import type { IPedidoDetalhe } from "interfaces/loja/pedido";
import type { TipoEntrega } from "interfaces/loja/pedidoOperacional";
import { copyRetiradaUi } from "interfaces/loja/pedidoRetirada";
import { keyLocalStorage } from "helpers/constants";
import type { User } from "models/users/type_user";
import {
  confirmarRetiradaPedidoLoja,
  marcarProntoParaRetiradaPedidoLoja,
} from "services/lojaPedidos";
import { messageFromApiError } from "utils/pedidoApiResponse";
import {
  buildPedidoOperacionalInput,
  resolvePedidoOperacional,
} from "utils/pedidoOperacional";
import { useMaterialUIController, setMessage } from "context";
import PedidoRetiradaResumo from "./PedidoRetiradaResumo";

type PedidoRetiradaOperacionalProps = {
  pedidoId: string;
  detalhe: IPedidoDetalhe | null;
  tipoEntrega: TipoEntrega;
  onAfterAction: (patch?: Partial<IPedidoDetalhe>) => Promise<void>;
};

function readSessionUser(): User | null {
  try {
    const raw = localStorage.getItem(keyLocalStorage.USER_STORAGE);
    if (!raw) return null;
    return JSON.parse(raw) as User;
  } catch {
    return null;
  }
}

function pickPedidoPatchFromApi(data: unknown): Partial<IPedidoDetalhe> {
  if (!data || typeof data !== "object") return {};
  const o = data as Record<string, unknown>;
  const keys = [
    "status",
    "statusRetirada",
    "tipoEntrega",
    "concluidoEm",
  ] as const;
  const out: Partial<IPedidoDetalhe> = {};
  for (const k of keys) {
    if (k in o) (out as Record<string, unknown>)[k] = o[k];
  }
  return out;
}

export default function PedidoRetiradaOperacional({
  pedidoId,
  detalhe,
  tipoEntrega,
  onAfterAction,
}: PedidoRetiradaOperacionalProps) {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const borderSoft =
    mode === "dark" ? alpha("#c8e6c9", 0.45) : alpha("#7cb342", 0.22);
  const [, dispatch] = useMaterialUIController();
  const copy = copyRetiradaUi(tipoEntrega);

  const input = useMemo(
    () =>
      detalhe
        ? buildPedidoOperacionalInput(
            detalhe as unknown as Parameters<
              typeof buildPedidoOperacionalInput
            >[0],
            detalhe.pagamentos ?? []
          )
        : null,
    [detalhe]
  );

  const o = useMemo(
    () => (input ? resolvePedidoOperacional(input) : null),
    [input]
  );

  const ret = detalhe?.retiradaPedido;
  const user = useMemo(() => readSessionUser(), []);

  const responsavelLabel = useMemo(() => {
    const nome = ret?.confirmadoPor?.nome?.trim();
    if (nome) return nome;
    const uid = ret?.confirmadoPorUserId;
    if (uid && user?.id === uid) return user.nome || "Você";
    if (uid) return `${uid.slice(0, 8)}…`;
    return "—";
  }, [
    ret?.confirmadoPor?.nome,
    ret?.confirmadoPorUserId,
    user?.id,
    user?.nome,
  ]);

  const [nomeRecebedor, setNomeRecebedor] = useState("");
  const [documentoRecebedor, setDocumentoRecebedor] = useState("");
  const [telefoneRecebedor, setTelefoneRecebedor] = useState("");
  const [submitting, setSubmitting] = useState<"pronto" | "confirmar" | null>(
    null
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    setNomeRecebedor((ret?.nomeRecebedor ?? "").trim());
    setDocumentoRecebedor((ret?.documentoRecebedor ?? "").trim());
    setTelefoneRecebedor((ret?.telefoneRecebedor ?? "").trim());
  }, [ret?.nomeRecebedor, ret?.documentoRecebedor, ret?.telefoneRecebedor]);

  const concluido = useMemo(() => {
    if (!o || !detalhe) return false;
    const sr = o.statusRetirada;
    return (
      sr === "RETIRADO" ||
      sr === "ENTREGUE_NO_EVENTO" ||
      String(detalhe.status) === "ENTREGUE"
    );
  }, [o, detalhe]);

  const podeMarcarPronto =
    o &&
    o.statusSeparacao === "SEPARADO" &&
    (o.statusRetirada === "PENDENTE" || o.statusRetirada === null);

  const podeConfirmar = useMemo(() => {
    if (!o || o.statusSeparacao !== "SEPARADO" || concluido) return false;
    const sr = o.statusRetirada;
    if (tipoEntrega === "RETIRADA_EVENTO") {
      return (
        sr === "PRONTO_PARA_RETIRADA" ||
        sr === "PENDENTE" ||
        sr === null
      );
    }
    return sr === "PRONTO_PARA_RETIRADA";
  }, [o, tipoEntrega, concluido]);

  const runPost = useCallback(
    async (
      kind: "pronto" | "confirmar",
      fn: () => Promise<{ data: unknown; status: number }>
    ) => {
      setError(null);
      setSuccess(null);
      setSubmitting(kind);
      try {
        const { data, status } = await fn();
        const body = data as {
          success?: boolean;
          message?: string;
          data?: unknown;
        };
        if (status === 200 && body.success === true) {
          const patch = pickPedidoPatchFromApi(body.data);
          const now = new Date().toISOString();
          await onAfterAction({
            ...patch,
            retiradaPedido:
              kind === "pronto"
                ? {
                    ...detalhe?.retiradaPedido,
                    status: "PRONTO_PARA_RETIRADA",
                  }
                : {
                    ...detalhe?.retiradaPedido,
                    status:
                      tipoEntrega === "RETIRADA_EVENTO"
                        ? "ENTREGUE_NO_EVENTO"
                        : "RETIRADO",
                    nomeRecebedor: nomeRecebedor.trim(),
                    documentoRecebedor: documentoRecebedor.trim() || null,
                    telefoneRecebedor: telefoneRecebedor.trim() || null,
                    retiradoEm: now,
                    confirmadoPorUserId: user?.id ?? ret?.confirmadoPorUserId,
                  },
          });
          const msg =
            body.message ||
            (kind === "pronto"
              ? "Atualizado com sucesso."
              : "Confirmação registrada.");
          setSuccess(msg);
          setMessage(dispatch, { open: true, severity: "success", text: msg });
        } else {
          const msg = messageFromApiError(
            data,
            "Não foi possível concluir a ação."
          );
          setError(msg);
          setMessage(dispatch, { open: true, severity: "error", text: msg });
        }
      } catch (e: unknown) {
        const text =
          e instanceof Error ? e.message : "Falha na comunicação com o servidor.";
        setError(text);
        setMessage(dispatch, { open: true, severity: "error", text });
      } finally {
        setSubmitting(null);
      }
    },
    [
      onAfterAction,
      detalhe?.retiradaPedido,
      nomeRecebedor,
      documentoRecebedor,
      telefoneRecebedor,
      user?.id,
      ret?.confirmadoPorUserId,
      tipoEntrega,
      dispatch,
    ]
  );

  const onPronto = useCallback(() => {
    runPost("pronto", () => marcarProntoParaRetiradaPedidoLoja(pedidoId));
  }, [pedidoId, runPost]);

  const onConfirmar = useCallback(() => {
    if (!nomeRecebedor.trim()) {
      setError("Informe o nome de quem retira ou recebe.");
      return;
    }
    runPost("confirmar", () =>
      confirmarRetiradaPedidoLoja(pedidoId, {
        nomeRecebedor: nomeRecebedor.trim(),
        documentoRecebedor: documentoRecebedor.trim() || undefined,
        telefoneRecebedor: telefoneRecebedor.trim() || undefined,
      })
    );
  }, [
    pedidoId,
    nomeRecebedor,
    documentoRecebedor,
    telefoneRecebedor,
    runPost,
  ]);

  if (!detalhe || !o) return null;

  return (
    <Stack spacing={2}>
      {concluido && (
        <Alert severity="success" icon={<CheckCircleOutlineIcon fontSize="inherit" />}>
          <Typography variant="body2" fontWeight={600}>
            {copy.concluidoTitulo}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {copy.concluidoTexto}
          </Typography>
        </Alert>
      )}

      {o.statusSeparacao !== "SEPARADO" && !concluido && (
        <Alert severity="warning">
          Conclua a separação antes de operar a retirada ou entrega no evento.
        </Alert>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <PedidoRetiradaResumo
        statusRetirada={o.statusRetirada}
        ret={ret}
        responsavelLabel={responsavelLabel}
        concluido={concluido}
        situacaoTitulo={copy.situacaoTitulo}
        codigoLabel={copy.codigoLabel}
        labelNome={copy.resumoNome}
        labelDoc={copy.resumoDoc}
        labelTel={copy.resumoTel}
        labelData={copy.resumoData}
      />

      <Card
        elevation={0}
        sx={{
          borderRadius: 2,
          border: `1px solid ${borderSoft}`,
          opacity: concluido ? 0.88 : 1,
        }}
      >
        <MDBox p={2.5}>
          <Typography variant="button" fontWeight={700}>
            Ações
          </Typography>
          {concluido ? (
            <Typography variant="body2" color="text.secondary" mt={1}>
              Fluxo encerrado — apenas consulta dos dados acima.
            </Typography>
          ) : (
            <>
              <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
                {copy.preparacaoHint}
              </Typography>
              <MDBox mt={2} mb={2}>
                <MDButton
                  variant="gradient"
                  color="dark"
                  disabled={!podeMarcarPronto || !!submitting}
                  onClick={onPronto}
                >
                  {submitting === "pronto" ? "Processando…" : copy.btnPronto}
                </MDButton>
              </MDBox>

              <Typography variant="caption" color="text.secondary" display="block" mt={1}>
                {copy.confirmHint}
              </Typography>
              <MDBox
                display="grid"
                gridTemplateColumns={{ xs: "1fr", sm: "1fr 1fr" }}
                gap={2}
                mt={2}
                mb={2}
              >
                <MDInput
                  label={`${copy.recebedorLabel} *`}
                  value={nomeRecebedor}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setNomeRecebedor(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeConfirmar}
                />
                <MDInput
                  label={copy.docLabel}
                  value={documentoRecebedor}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setDocumentoRecebedor(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeConfirmar}
                />
                <MDInput
                  label={copy.telLabel}
                  value={telefoneRecebedor}
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    setTelefoneRecebedor(e.target.value)
                  }
                  fullWidth
                  disabled={!!submitting || !podeConfirmar}
                  sx={{ gridColumn: { sm: "1 / -1" } }}
                />
              </MDBox>
              <MDButton
                variant="outlined"
                color="success"
                disabled={
                  !!submitting || !podeConfirmar || !nomeRecebedor.trim()
                }
                onClick={onConfirmar}
              >
                {submitting === "confirmar"
                  ? "Processando…"
                  : copy.btnConfirmar}
              </MDButton>
            </>
          )}
        </MDBox>
      </Card>
    </Stack>
  );
}
